'use client'

// Shared editor section modal shell (doc 29 §5.2: SKELETON — structure only,
// every visual class arrives through the surface skin's optional `modal`
// group). Used by the Configure and Attachments modals on std/site/gallery/
// forum; mailbox keeps its own dialog and never mounts this.
//
// Contract obligations honored here:
// - children stay MOUNTED while closed (the overlay is class-hidden), so the
//   settings/attachment inputs remain part of the form for legacy save;
// - the modal renders inside the editor.root subtree (no portal), so
//   EditorFormDefaults hydration and legacy input binding keep resolving;
// - render is deterministic (no browser APIs outside effects/handlers).

import { useEffect, useRef, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { classNames } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { useEditorSurface, type EditorModalSkin } from '../../EditorSurfaceContext'

// No anchor selector: the skeleton no-CSS scan rejects that literal shape,
// and modal content is button/input driven anyway.
const focusableSelector = [
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function visibleFocusableElements(root: HTMLElement | null) {
  if (!root) return []
  return Array.from(root.querySelectorAll<HTMLElement>(focusableSelector)).filter(element => (
    !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0
  ))
}

function trapTab(event: ReactKeyboardEvent<HTMLElement>, root: HTMLElement | null) {
  if (event.key !== 'Tab') return
  const focusable = visibleFocusableElements(root)
  if (!focusable.length) {
    event.preventDefault()
    root?.focus()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

export interface EditorModalNavItem {
  id: string
  label: string
}

export function useEditorModalSkin(): EditorModalSkin {
  const { skin } = useEditorSurface()
  if (!skin.modal) {
    throw new Error('EditorModal requires the surface EditorSkin to supply the optional `modal` token group')
  }
  return skin.modal
}

export function EditorModal({
  open,
  onClose,
  title,
  icon,
  nav,
  activeNavId,
  onSelectNav,
  children,
}: {
  open: boolean
  onClose: () => void
  title: string
  icon?: ReactNode
  nav?: EditorModalNavItem[]
  activeNavId?: string
  onSelectNav?: (id: string) => void
  children: ReactNode
}) {
  const modal = useEditorModalSkin()
  const dialogRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) return
    previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const frame = window.requestAnimationFrame(() => {
      const preferred = dialogRef.current?.querySelector<HTMLElement>('[data-md-modal-close="true"]')
      ;(preferred || dialogRef.current)?.focus()
    })

    return () => {
      window.cancelAnimationFrame(frame)
      document.body.style.overflow = previousOverflow
      previousFocusRef.current?.focus()
    }
  }, [open])

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation()
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
      return
    }
    trapTab(event, dialogRef.current)
  }

  return (
    <div
      className={classNames('md-editor-modal', open ? modal.overlayOpen : modal.overlayClosed)}
      aria-hidden={!open}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal={open || undefined}
        aria-label={title}
        tabIndex={-1}
        className={classNames('md-editor-modal__dialog', modal.dialog)}
        onKeyDown={handleKeyDown}
      >
        <header className={classNames('md-editor-modal__header', modal.header)}>
          {icon ? <div className={classNames('md-editor-modal__icon', modal.headerIcon)}>{icon}</div> : null}
          <h2 className={classNames('md-editor-modal__title', modal.title)}>{title}</h2>
          <button
            type="button"
            data-md-modal-close="true"
            className={classNames('md-editor-modal__close', modal.closeButton)}
            onClick={onClose}
            title="Close"
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className={classNames('md-editor-modal__body', modal.body)}>
          {nav && nav.length ? (
            <nav className={classNames('md-editor-modal__nav', modal.nav)} aria-label={`${title} sections`}>
              {nav.map(item => (
                <button
                  key={item.id}
                  type="button"
                  className={classNames(
                    'md-editor-modal__nav-item',
                    modal.navItem,
                    item.id === activeNavId && 'md-editor-modal__nav-item--active',
                    item.id === activeNavId ? modal.navItemActive : modal.navItemInactive,
                  )}
                  aria-current={item.id === activeNavId || undefined}
                  onClick={() => onSelectNav?.(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          ) : null}
          <div className={classNames('md-editor-modal__content', modal.content)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
